import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getComponents, getSystemStatus, getScheduledMaintenances } from "../api/statuspage";
import { IAPIError } from "../interfaces/common";
import { intlFormat } from "date-fns";
import Cookies from "js-cookie";

export const systemStatusQueryKey = ['systemStatus'] as const;
export const incidentDetailsQueryKey = ['incidentDetails'] as const;
export const maintenanceDetailsQueryKey = ['maintenanceDetails'] as const;

const componentID = 't1b831tgjrl2';

const getComponent = (components: any[]) =>
  components?.find((el: any) => el.id === componentID);

const getFormattedTime = (date: string) =>
  intlFormat(
    new Date(date),
    {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
      timeZone: 'America/New_York',
    },
    { locale: 'en-US' }
  );

export const useSystemStatus = (
  enabled?: boolean
): UseQueryResult<string, IAPIError> => {
  return useQuery<string, IAPIError>({
    queryKey: systemStatusQueryKey,
    queryFn: async ({ signal }) => {
      const componentStatusResponse = await getComponents(signal);
      const component = getComponent(componentStatusResponse.components);
      return component?.status;
    },
    enabled
  });
};

export const useIncidentDetails = (
  enabled?: boolean
): UseQueryResult<any, IAPIError> => {
  return useQuery<any, IAPIError>({
    queryKey: incidentDetailsQueryKey,
    queryFn: async ({ signal }) => {
      const response = await getSystemStatus(signal);
      const filteredIncident = response.incidents.find((el: any) =>
        getComponent(el?.components)
      );
      if (filteredIncident) {
        const incident = getComponent(filteredIncident?.components);
        const savedStatus = Cookies.get(`markedDone${incident?.id}`);
        return {
          id: incident?.id,
          name: filteredIncident?.name,
          componentStatus: incident?.status,
          incidentStatus: filteredIncident?.incident_updates?.[0]?.status,
          description: filteredIncident?.incident_updates?.[0]?.body,
          updatedAt: getFormattedTime(
            filteredIncident.incident_updates?.[0]?.updated_at
          ),
          link: filteredIncident?.shortlink,
          enabled: !savedStatus,
        };
      }
      return null;
    },
    enabled
  });
};

export const useMaintenanceDetails = (
  enabled?: boolean
): UseQueryResult<any, IAPIError> => {
  return useQuery<any, IAPIError>({
    queryKey: maintenanceDetailsQueryKey,
    queryFn: async ({ signal }) => {
      const response = await getScheduledMaintenances(signal);
      const filteredIncident = response.scheduled_maintenances
        .filter((ele: any) => (ele.status !== 'completed'))
        .find((el: any) => getComponent(el.components));
      if (filteredIncident) {
        const maintenance = getComponent(filteredIncident.components);
        const savedStatus = Cookies.get(`markedDone${maintenance?.id}`);
        return {
          id: maintenance?.id,
          name: filteredIncident?.name,
          componentStatus: maintenance?.status,
          incidentStatus: filteredIncident?.incident_updates?.[0]?.status,
          description: filteredIncident?.incident_updates?.[0]?.body,
          updatedAt: getFormattedTime(
            filteredIncident?.incident_updates?.[0]?.updated_at
          ),
          link: filteredIncident?.shortlink,
          scheduledFor: getFormattedTime(filteredIncident?.scheduled_for),
          scheduledUntil: getFormattedTime(filteredIncident?.scheduled_until),
          enabled: !savedStatus,
        };
      }
      return null;
    },
    enabled
  });
};
